import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import Header from "@components/Header";
import CatalogFooter from "@components/CatalogFooter";
import SubscriptionMovieCard from "@components/SubscriptionMovieCard";
import { NEW_IN_SUBSCRIPTION } from "@features/home/data/homeData";
import { contentApi, mediaUrl } from "@services/contentApi";
import "@styles/catalog.css";

const PAGE_COPY = {
  movies: "Фильмы",
  series: "Сериалы",
  cartoons: "Мультфильмы",
  anime: "Аниме",
  all: "Каталог",
};

const FALLBACK_ORDER = [0, 1, 2, 3, 4, 5, 2, 1, 2, 3, 1, 3];

const FALLBACK_CATALOG = FALLBACK_ORDER.map((sourceIndex, index) => {
  const source = NEW_IN_SUBSCRIPTION[sourceIndex % NEW_IN_SUBSCRIPTION.length];

  return {
    ...source,
    id: `catalog-fallback-${index}`,
    href: "/movies",
    rating: "9,0",
    year: "2021",
    type: "Фильм",
    numericYear: 2021,
    numericRating: 9,
    genres: ["Комедия", "Приключения"],
    countries: ["США"],
    studio: ["Warner Bros.", "Disney", "Netflix"][index % 3],
    status: "Опубликовано",
  };
});

function matchesKind(item, kind) {
  const category = String(item.category?.title || "").toLowerCase();
  if (kind === "all") return true;

  const isAnime = category.includes("аниме") || category.includes("anime");
  const isCartoon = category.includes("мульт") || category.includes("animation") || category.includes("cartoon");

  if (kind === "anime") return isAnime;
  if (kind === "cartoons") return isCartoon;
  if (kind === "series") return item.type === "series" && !isAnime && !isCartoon;
  return item.type === "movie" && !isAnime && !isCartoon;
}

function mapApiItem(item, index) {
  const year = item.releaseDate ? new Date(item.releaseDate).getFullYear() : 0;
  const fallback = FALLBACK_CATALOG[index % FALLBACK_CATALOG.length];
  const rating = Number(item.rating || 0);

  return {
    id: item._id,
    href: `/watch/${item.slug || item._id}`,
    title: item.title,
    year: year || "—",
    numericYear: year,
    type: item.type === "series" ? "Сериал" : "Фильм",
    rating: rating ? rating.toFixed(1).replace(".", ",") : fallback.rating,
    numericRating: rating,
    img: mediaUrl(item.posterUrl || item.backdropUrl) || fallback.img,
    bg: fallback.bg,
    genres: [...new Set(item.genres || [])],
    countries: item.countries || [],
    studio: item.studio || item.productionCompany || "",
    status: item.status === "published" ? "Опубликовано" : item.status || "Черновик",
  };
}

function getUniqueValues(items, getValue) {
  return [...new Set(items.flatMap((item) => {
    const value = getValue(item);
    return Array.isArray(value) ? value : value ? [value] : [];
  }))].sort((a, b) => String(a).localeCompare(String(b), "ru"));
}

export default function CatalogPage({ kind = "movies" }) {
  const [searchParams] = useSearchParams();
  const requestedGenre = searchParams.get("genre") || "";
  const title = PAGE_COPY[kind] || PAGE_COPY.movies;
  const [items, setItems] = useState(FALLBACK_CATALOG);
  const [loading, setLoading] = useState(true);
  const [openFilter, setOpenFilter] = useState(null);
  const filterBarRef = useRef(null);
  const [filters, setFilters] = useState({
    genre: requestedGenre,
    country: "",
    studio: "",
    year: "",
    type: "",
    status: "",
    sort: "new",
  });

  useEffect(() => {
    let active = true;
    setLoading(true);

    contentApi.listContents({ limit: 200, status: "published" })
      .then((data) => {
        if (!active) return;
        const mapped = (Array.isArray(data) ? data : [])
          .filter((item) => matchesKind(item, kind))
          .map(mapApiItem);
        setItems(mapped.length ? mapped : FALLBACK_CATALOG);
      })
      .catch(() => active && setItems(FALLBACK_CATALOG))
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [kind]);

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      if (!filterBarRef.current?.contains(event.target)) setOpenFilter(null);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpenFilter(null);
    };

    document.addEventListener("pointerdown", handleOutsidePointer);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const options = useMemo(() => ({
    genres: getUniqueValues(items, (item) => item.genres),
    countries: getUniqueValues(items, (item) => item.countries),
    studios: getUniqueValues(items, (item) => item.studio),
    years: [...new Set(items.map((item) => item.numericYear).filter(Boolean))].sort((a, b) => b - a),
    types: getUniqueValues(items, (item) => item.type),
    statuses: getUniqueValues(items, (item) => item.status),
  }), [items]);

  const visibleItems = useMemo(() => {
    const result = items.filter((item) => {
      const matchesGenre = !filters.genre || item.genres?.includes(filters.genre);
      const matchesCountry = !filters.country || item.countries?.includes(filters.country);
      const matchesStudio = !filters.studio || item.studio === filters.studio;
      const matchesYear = !filters.year || item.numericYear === Number(filters.year);
      const matchesType = !filters.type || item.type === filters.type;
      const matchesStatus = !filters.status || item.status === filters.status;

      return matchesGenre && matchesCountry && matchesStudio && matchesYear && matchesType && matchesStatus;
    });

    return [...result].sort((a, b) => filters.sort === "rating"
      ? b.numericRating - a.numericRating
      : b.numericYear - a.numericYear);
  }, [filters, items]);

  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));
  const selectFilter = (name, value) => {
    setFilter(name, value);
    setOpenFilter(null);
  };
  const reset = () => setFilters({ genre: "", country: "", studio: "", year: "", type: "", status: "", sort: "new" });

  return (
    <div className="catalog-page">
      <Header />

      <main className="catalog-container catalog-main">
        <div className="catalog-breadcrumbs" aria-label="Хлебные крошки">
          <span>Главная</span><b>/</b><strong>{title}</strong>
        </div>

        <div className="catalog-heading-row">
          <h1>{title}</h1>

          <div className="catalog-filter-bar" aria-label="Фильтры каталога" ref={filterBarRef}>
            <CatalogSelect filterKey="genre" value={filters.genre} onChange={(value) => selectFilter("genre", value)} label="Жанры" options={options.genres} openFilter={openFilter} setOpenFilter={setOpenFilter} />
            <CatalogSelect filterKey="country" value={filters.country} onChange={(value) => selectFilter("country", value)} label="Страны" options={options.countries} openFilter={openFilter} setOpenFilter={setOpenFilter} />
            <CatalogSelect filterKey="studio" value={filters.studio} onChange={(value) => selectFilter("studio", value)} label="Студии" options={options.studios} openFilter={openFilter} setOpenFilter={setOpenFilter} />
            <CatalogSelect filterKey="year" value={filters.year} onChange={(value) => selectFilter("year", value)} label="Годы" options={options.years} openFilter={openFilter} setOpenFilter={setOpenFilter} />
            <CatalogSelect filterKey="type" value={filters.type} onChange={(value) => selectFilter("type", value)} label="Тип" options={options.types} openFilter={openFilter} setOpenFilter={setOpenFilter} />
            <CatalogSelect filterKey="status" value={filters.status} onChange={(value) => selectFilter("status", value)} label="Статус" options={options.statuses} openFilter={openFilter} setOpenFilter={setOpenFilter} />

            <div className="catalog-sort" role="group" aria-label="Сортировка">
              <button className={filters.sort === "new" ? "is-active" : ""} onClick={() => { setFilter("sort", "new"); setOpenFilter(null); }} type="button">По новизне</button>
              <button className={filters.sort === "rating" ? "is-active" : ""} onClick={() => { setFilter("sort", "rating"); setOpenFilter(null); }} type="button">По рейтингу</button>
            </div>
          </div>
        </div>

        <div className={`catalog-grid ${loading ? "is-loading" : ""}`}>
          {visibleItems.map((item) => <SubscriptionMovieCard key={item.id} item={item} />)}
        </div>

        {!loading && !visibleItems.length && (
          <div className="catalog-empty">
            <h2>Ничего не найдено</h2>
            <p>Измените параметры фильтрации.</p>
            <button onClick={reset} type="button">Сбросить фильтры</button>
          </div>
        )}
      </main>

      <CatalogFooter />
    </div>
  );
}

function CatalogSelect({ filterKey, value, onChange, label, options, openFilter, setOpenFilter }) {
  const isOpen = openFilter === filterKey;

  return (
    <div className={`catalog-filter-select ${isOpen ? "is-open" : ""}`}>
      <button
        className="catalog-filter-trigger"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setOpenFilter(isOpen ? null : filterKey)}
      >
        <span>{value || label}</span>
        <ChevronDown size={18} strokeWidth={1.8} />
      </button>

      <div className="catalog-filter-menu" role="listbox" aria-label={label}>
        <button className={`catalog-filter-option ${!value ? "is-selected" : ""}`} onClick={() => onChange("")} type="button" role="option" aria-selected={!value}>
          <span>Все варианты</span>{!value && <i />}
        </button>
        {options.length ? options.map((option) => (
          <button className={`catalog-filter-option ${String(value) === String(option) ? "is-selected" : ""}`} key={option} onClick={() => onChange(String(option))} type="button" role="option" aria-selected={String(value) === String(option)}>
            <span>{option}</span>{String(value) === String(option) && <i />}
          </button>
        )) : <div className="catalog-filter-empty">Нет вариантов</div>}
      </div>
    </div>
  );
}
